import React, { useState } from 'react';
import { RosaryIcon } from '../../icons/RosaryIcon';

interface Setup54DayScreenProps {
  onContinue: (data: { intention: string; reminderTime: string; preReminder: boolean; enableReminders: boolean }) => void;
}

const suggestedIntentions = [
  "For a family member's conversion",
  "For healing and peace",
  "For guidance in a difficult decision",
  "For strength in faith",
  "For a spouse or future spouse",
  "Custom intention",
];

export const Setup54DayScreen: React.FC<Setup54DayScreenProps> = ({ onContinue }) => {
  const [intention, setIntention] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [enableReminders, setEnableReminders] = useState(true);
  const [reminderTime, setReminderTime] = useState('19:00'); // 7:00 PM default
  const [preReminder, setPreReminder] = useState(false);

  const handleSuggestionSelect = (suggestion: string) => {
    if (suggestion === 'Custom intention') {
      setIntention('');
      setShowSuggestions(false);
    } else {
      setIntention(suggestion);
      setShowSuggestions(false);
    }
  };

  const handleContinue = () => {
    if (intention.trim()) {
      onContinue({
        intention: intention.trim(),
        reminderTime,
        preReminder,
        enableReminders,
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <RosaryIcon size={64} className="text-indigo-600 dark:text-indigo-400" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Let's set your intention for this special novena
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          The 54-day novena is most powerful when prayed with a specific intention
        </p>
      </div>

      {/* Intention Input */}
      <div className="space-y-6 flex-1">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Your intention
          </label>
          <textarea
            value={intention}
            onChange={(e) => setIntention(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Enter your prayer intention..."
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 transition-all duration-200 resize-none"
            rows={3}
          />
        </div>

        {/* Suggested Intentions */}
        {showSuggestions && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Need inspiration? Try these:
            </p>
            <div className="space-y-2">
              {suggestedIntentions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors duration-200"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Time Reminder Setup */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Daily prayer reminders
            </h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableReminders}
                onChange={(e) => setEnableReminders(e.target.checked)}
                className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-2 focus:ring-indigo-600/20 transition-all duration-200"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable reminders
              </span>
            </label>
          </div>

          {enableReminders && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  When would you like to pray?
                </label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 transition-all duration-200"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preReminder}
                  onChange={(e) => setPreReminder(e.target.checked)}
                  className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-2 focus:ring-indigo-600/20 transition-all duration-200"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Remind me 15 minutes before
                </span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-8">
        <button
          onClick={handleContinue}
          disabled={!intention.trim()}
          className={`
            w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform
            ${
              intention.trim()
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white hover:scale-105 shadow-lg'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Begin My 54-Day Journey
        </button>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
          54 days of prayer starting today
        </p>
      </div>
    </div>
  );
};